import { FC, useEffect, useState } from "react";
import SubmitItem from "../../../components/template/SubmitItem/SubmitItem";
import { FieldValues, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, useNavigate } from "react-router-dom";
import {
  INDICATOR_DATA_SEED,
  INDICATOR_DATA_SEED_MODEL,
} from "../../../utils/endpoints";
import { IndicatorUploadDataSchema } from "../data/IndicatorUploadDataSchema";
import UploadDataForm from "../forms/IndicatorUploadDataForm/UploadDataForm";
import UploadCompletedTemplateForm from "../forms/IndicatorUploadDataForm/UploadCompletedTemplateForm";
import { StepperConfig } from "../../../types/Components/Stepper/StepperConfig";
import { AlertDialogConfig } from "../../../types/Components/Alert/AlertDialogConfig";
import { AlertConfig } from "../../../types/Components/Alert/AlertConfig";
import api from "../../../utils/api";
import { useScroll } from "../../../provider/ScrollProvider";
import { handleSubmitFile } from "../../../helpers/handleSubmitFile";
import { Dayjs } from "dayjs";
import { uploadFile } from "../../../helpers/uploadFile";
import { ExcelErrors } from "../../../types/Indicator/ExcelErrors";
import { TableConfig } from "../../../types/Components/Table/TableConfig";
import TableExcelErrors from "../TableExcelErrors";
import useTableRendering from "../../../hooks/table/useTableRendering";
import { PAGE_SIZE_OPTIONS } from "../../../utils/constants";
import { PaginationConfig } from "../../../types/Components/Table/PaginationConfig";
import { Toast } from "../../../components/atom/Toast/Toast";

const UploadDataIndicator: FC = () => {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [hasWarning, setHasWarning] = useState(false);
  const [fileErrors, setFileErrors] = useState<ExcelErrors[]>([]);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(false);
  const [returnButtonDisabled] = useState(false);
  const [isUploadExecuted, setIsUploadExecuted] = useState(false);
  const [partnerName, setPartnerName] = useState<string>("");
  const [uploadDate, setUploadDate] = useState<string>("");
  const [isLoaded] = useState(true);
  const [uploadedFile, setUploadedFile] = useState<File | null | undefined>(
    undefined,
  );
  const [selectedYear, setSelectedYear] = useState<Dayjs | null>(null);
  const { addToast } = Toast();

  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    watch,
    trigger,
    resetField,
  } = useForm({
    resolver: zodResolver(IndicatorUploadDataSchema),
    mode: "onChange",
    shouldUseNativeValidation: false,
  });

  const partnerOption = watch("partnerOption");
  const frequencyOption = watch("frequencyOption");
  const frequencyData = watch("frequencyData");
  const year = watch("year");

  useEffect(() => {
    resetField("frequencyData");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequencyOption]);

  const onLoad = async (data: FieldValues) => {
    const bodyRequest = {
      partnerId: data.partnerOption.id,
      collectionFrequencyId: data.frequencyOption.id,
      year: data.year ? data.year.year() : null,
      dataPeriodId: data.frequencyData ? data.frequencyData.id : null,
    };

    const file = await handleSubmitFile(() =>
      api.post(INDICATOR_DATA_SEED_MODEL, bodyRequest, {
        responseType: "arraybuffer",
      }),
    );

    file && setUploadedFile(file);
    setHasWarning(false);
  };

  const steps = [
    { title: "Informações sobre os dados", description: "Passo 1" },
    { title: "Carregar template preenchido", description: "Passo 2" },
  ];

  const [currentStep, setCurrentStep] = useState(0);
  const { scrollToTop } = useScroll();

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      const isValid = await trigger();
      if (!isValid) {
        return;
      }
      handleSubmit(onLoad)();
      scrollToTop();
      setCurrentStep((prevStep) => prevStep + 1);
      setNextButtonDisabled(true);
    }
  };

  const handleUploadFile = async () => {
    if (uploadedFile && !isUploadExecuted) {
      const response = await uploadFile(
        api.post,
        uploadedFile,
        INDICATOR_DATA_SEED,
        "application/octet-stream",
      );
      if (response?.erros && Array.isArray(response.erros)) {
        setFileErrors((prevErrors) => [...prevErrors, ...response.erros]);
      }
      setIsUploadExecuted(true);
    } else {
      addToast({
        status: "warning",
        title: "Alerta",
        description: "Envie um novo arquivo para verificação.",
      });
    }
  };

  const handleBack = () => {
    setCurrentStep((prevStep) => prevStep - 1);
    setUploadedFile(undefined);
    setFileErrors([]);
    setNextButtonDisabled(false);
  };

  useEffect(() => {
    if (partnerOption) {
      setPartnerName(partnerOption.name);
    }
    if (frequencyData && year) {
      setUploadDate(`${frequencyData.name} de ${year.year()}`);
    } else if (frequencyData) {
      setUploadDate(frequencyData.name);
    } else if (year) {
      setUploadDate(year.year());
    } else {
      setUploadDate("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    if (uploadedFile !== null && fileErrors.length === 0) {
      setNextButtonDisabled(false);
    } else {
      setNextButtonDisabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadExecuted]);

  useEffect(() => {
    setFileErrors([]);
    setIsUploadExecuted(false);
  }, [uploadedFile]);

  const stepperConfig: StepperConfig = {
    nextButtonName: "Fechar",
    steps,
    onNext:
      currentStep === steps.length - 1
        ? () => navigate("/gestao-de-indicadores")
        : handleNext,
    onBack: handleBack,
    isNextActive: true,
    isFinalStep: currentStep === steps.length - 1,
    currentStep,
    isButtonDisabled: nextButtonDisabled,
    isReturnButtonDisabled: returnButtonDisabled,
  };

  const alertDialogConfig: AlertDialogConfig = {
    setIsAlertOpen,
    isAlertOpen,
  };

  const alertConfig: AlertConfig = {
    submitAlertTitle: "Já existem dados no sistema para o período inserido.",
    submitAlertStatus: "warning",
    submitAlertDescription:
      "O carregamento de novos dados irá substituir os dados anteriores. Insira apenas valores para os indicadores que pretende corrigir.",
    submitAlertBorderColor: "orange.500",
  };

  const tableConfig: TableConfig = {
    tableName: `Lista de erros ${fileErrors && ` (${fileErrors.length})`}`,
    dataTable: fileErrors,
    setDataTable: setFileErrors,
    renderTable: (fileErrors) => <TableExcelErrors dataTable={fileErrors} />,
    isLoaded,
  };

  const { currentPage, totalPages, handlePageChange, renderContent } =
    useTableRendering(tableConfig, pageSize);

  const paginationConfig: PaginationConfig = {
    pageSize,
    currentPage,
    totalPages,
    handlePageChange,
    renderContent,
    setPageSize,
  };

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <SubmitItem
        title="Carregar dados no SISAN (Excel)"
        hasSubmitFooter
        hasStepper
        hasAlert={hasWarning}
        stepperConfig={stepperConfig}
        alertDialogConfig={alertDialogConfig}
        alertConfig={alertConfig}
        isLoaded={isLoaded}
        isDirty={isDirty}
      >
        {currentStep === 0 && (
          <UploadDataForm
            errors={errors}
            control={control}
            watch={watch}
            hasWarning={hasWarning}
            setHasWarning={setHasWarning}
            frequencyOption={frequencyOption}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
          />
        )}
        {currentStep === 1 && (
          <UploadCompletedTemplateForm
            partnerName={partnerName}
            uploadDate={uploadDate}
            uploadedFile={uploadedFile}
            setUploadedFile={setUploadedFile}
            isUploadExecuted={isUploadExecuted}
            tableConfig={tableConfig}
            paginationConfig={paginationConfig}
            handleUploadFile={handleUploadFile}
          />
        )}
      </SubmitItem>
    </Form>
  );
};

export default UploadDataIndicator;
